import {
  DesktopWindows,
  DeveloperBoard,
  DevicesOther,
  Dns,
  DoNotStep,
  Hub,
  Monitor,
  RocketLaunch,
  Router,
  SdStorage,
  Sell,
  Thermostat,
  Timer,
  Tv,
  Update,
  VisibilityOff,
  Wifi,
} from '@mui/icons-material';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { FilterCollapsibleList } from '~/components/Filter';
import { useAppContext } from '~/contexts';
import { DeviceService } from '~/generated/graphql';
import { useDeviceFilters } from '~/pages/devices/list/context';
import type { DeviceList__FilterCount } from '~/pages/devices/queries/list.generated';

export interface DeviceFilterAccordionsProps {
  filterCounts: DeviceList__FilterCount | undefined;
}

// PTP modes share one form field, so each mode has its own filter label.
export const deviceServiceLabel = (t: TFunction, service: DeviceService) => {
  switch (service) {
    case DeviceService.PtpFollower:
      return t('pages.devices.filters.ptp_follower');
    case DeviceService.PtpLeader:
      return t('pages.devices.filters.ptp_leader');
    default:
      return t(`pages.devices.show.services.fields.${service.toLowerCase()}.label`);
  }
};

export const DeviceFilterAccordions = ({ filterCounts }: DeviceFilterAccordionsProps) => {
  const loading = !filterCounts;
  const { t } = useTranslation();
  const { currentNetwork, currentUser } = useAppContext();
  const { filters, setFilter, toggleFilter } = useDeviceFilters();
  const isAdmin = currentUser.admin;

  return (
    <>
      {filters.tags.items.length > 0 && (
        <FilterCollapsibleList
          Icon={Sell}
          expanded={filters.tags.open}
          items={filters.tags.items.map((item) => {
            const matched = filterCounts?.tags.find((c) => c.filter === item.value);
            return { ...item, count: matched?.count || 0 };
          })}
          label={t('common.labels.tags')}
          loading={loading}
          onClick={(_, items) => setFilter({ field: 'tags', items })}
          onExpand={() => toggleFilter('tags')}
        />
      )}
      {filters.groups.items.length > 0 && (
        <FilterCollapsibleList
          Icon={Hub}
          expanded={filters.groups.open}
          items={filters.groups.items.map((item) => {
            const matched = filterCounts?.groups.find((c) => c.filter === String(item.value));
            return { ...item, count: matched?.count || 0 };
          })}
          label={t('pages.devices.filters.groups')}
          loading={loading}
          onClick={(_, items) => setFilter({ field: 'groups', items })}
          onExpand={() => toggleFilter('groups')}
        />
      )}
      {filters.tvBrands.items.length > 0 && (
        <FilterCollapsibleList
          Icon={Tv}
          expanded={filters.tvBrands.open}
          items={filters.tvBrands.items.map((item) => {
            const matched = filterCounts?.tvBrands.find((c) => c.filter === String(item.value));
            return { ...item, count: matched?.count || 0 };
          })}
          label={t('pages.devices.filters.tv_brands')}
          loading={loading}
          onClick={(_, items) => setFilter({ field: 'tvBrands', items })}
          onExpand={() => toggleFilter('tvBrands')}
        />
      )}
      {filters.models.items.length > 0 && (
        <FilterCollapsibleList
          Icon={DevicesOther}
          expanded={filters.models.open}
          items={filters.models.items.map((item) => {
            const matched = filterCounts?.models.find((c) => c.filter === item.value);
            return { ...item, count: matched?.count || 0 };
          })}
          label={t('pages.devices.filters.models')}
          loading={loading}
          onClick={(_, items) => setFilter({ field: 'models', items })}
          onExpand={() => toggleFilter('models')}
        />
      )}
      {isAdmin && filters.displayServers.items.length > 0 && (
        <FilterCollapsibleList
          Icon={Monitor}
          expanded={filters.displayServers.open}
          items={filters.displayServers.items.map((item) => {
            const matched = filterCounts?.displayServers.find((c) => c.filter === item.value);
            return { ...item, count: matched?.count || 0 };
          })}
          label={t('pages.devices.filters.display_server')}
          loading={loading}
          onClick={(_, items) => setFilter({ field: 'displayServers', items })}
          onExpand={() => toggleFilter('displayServers')}
        />
      )}
      {filters.playerVersions.items.length > 0 && (
        <FilterCollapsibleList
          Icon={Update}
          expanded={filters.playerVersions.open}
          items={filters.playerVersions.items.map((item) => {
            const matched = filterCounts?.playerVersions.find((c) => c.filter === item.value);
            return { ...item, count: matched?.count || 0 };
          })}
          label={t('common.labels.version')}
          loading={loading}
          onClick={(_, items) => setFilter({ field: 'playerVersions', items })}
          onExpand={() => toggleFilter('playerVersions')}
        />
      )}
      <FilterCollapsibleList
        Icon={Router}
        expanded={filters.kind.open}
        items={filters.kind.items.map((item) => {
          const matched = filterCounts?.kind.find((c) => c.filter.toUpperCase() === item.value);
          return { ...item, count: matched?.count || 0 };
        })}
        label={t('common.labels.type')}
        loading={loading}
        onClick={(_, items) => setFilter({ field: 'kind', items })}
        onExpand={() => toggleFilter('kind')}
      />
      {isAdmin && (
        <FilterCollapsibleList
          Icon={DeveloperBoard}
          expanded={filters.arch.open}
          items={filters.arch.items.map((item) => {
            const matched = filterCounts?.arch.find((c) => c.filter.toUpperCase() === item.value);
            return { ...item, count: matched?.count || 0 };
          })}
          label={t('pages.devices.filters.arch')}
          loading={loading}
          onClick={(_, items) => setFilter({ field: 'arch', items })}
          onExpand={() => toggleFilter('arch')}
        />
      )}
      {currentNetwork.canManage.value && (
        <FilterCollapsibleList
          Icon={Dns}
          expanded={filters.services.open}
          items={filters.services.items
            // Only admins can manage SSH, so only they filter by it.
            .filter((item) => isAdmin || item.value !== DeviceService.SshService)
            .map((item) => {
              const matched = filterCounts?.services.find(
                (c) => c.filter.toUpperCase() === item.value,
              );
              return {
                ...item,
                count: matched?.count || 0,
                label: deviceServiceLabel(t, item.value),
              };
            })
            .sort((a, b) => a.label.localeCompare(b.label))}
          label={t('pages.devices.filters.services')}
          loading={loading}
          onClick={(_, items) => setFilter({ field: 'services', items })}
          onExpand={() => toggleFilter('services')}
        />
      )}
      <FilterCollapsibleList
        Icon={Wifi}
        expanded={filters.status.open}
        items={filters.status.items.map((item) => {
          const matched = filterCounts?.status.find((c) => c.filter.toUpperCase() === item.value);
          const statusLabels: Record<string, string> = {
            HEALTHY: t('pages.devices.list.status_toggle.online'),
            ERROR: t('pages.devices.list.status_toggle.offline'),
            WARN: t('pages.devices.list.status_toggle.warning'),
          };
          const label = statusLabels[item.value];
          return { ...item, label, count: matched?.count || 0 };
        })}
        label={t('common.labels.status')}
        loading={loading}
        onClick={(_, items) => setFilter({ field: 'status', items })}
        onExpand={() => toggleFilter('status')}
      />
      <FilterCollapsibleList
        Icon={SdStorage}
        expanded={filters.storageBelow.open}
        items={filters.storageBelow.items.map((item) => {
          const matched = filterCounts?.storageBelow.find((c) => c.filter === String(item.value));
          return {
            ...item,
            label: t('pages.devices.filters.storage_below', { value: item.value }),
            count: matched?.count || 0,
          };
        })}
        label={t('pages.devices.filters.storage_available')}
        loading={loading}
        onClick={(_, items) => setFilter({ field: 'storageBelow', items })}
        onExpand={() => toggleFilter('storageBelow')}
        singleSelect
      />
      <FilterCollapsibleList
        Icon={Thermostat}
        expanded={filters.temperatureAbove.open}
        items={filters.temperatureAbove.items.map((item) => {
          const matched = filterCounts?.temperatureAbove.find(
            (c) => c.filter === String(item.value),
          );
          return {
            ...item,
            count: matched?.count || 0,
          };
        })}
        label={t('pages.devices.filters.temperature')}
        loading={loading}
        onClick={(_, items) => setFilter({ field: 'temperatureAbove', items })}
        onExpand={() => toggleFilter('temperatureAbove')}
        singleSelect
      />
      {isAdmin && (
        <FilterCollapsibleList
          Icon={Timer}
          expanded={filters.uptimeBelow.open}
          items={filters.uptimeBelow.items.map((item) => {
            const matched = filterCounts?.uptimeBelow.find((c) => c.filter === String(item.value));
            return {
              ...item,
              label:
                item.value === 1
                  ? t('pages.devices.filters.uptime_within_one')
                  : t('pages.devices.filters.uptime_within', { value: item.value }),
              count: matched?.count || 0,
            };
          })}
          label={t('pages.devices.filters.uptime')}
          loading={loading}
          onClick={(_, items) => setFilter({ field: 'uptimeBelow', items })}
          onExpand={() => toggleFilter('uptimeBelow')}
          singleSelect
        />
      )}
      <FilterCollapsibleList
        Icon={DesktopWindows}
        expanded={filters.displayOn.open}
        items={filters.displayOn.items.map((item) => {
          const matched = filterCounts?.displayOn.find((c) => c.filter === String(item.value));
          const label = item.value
            ? t('pages.devices.show.details.display_on')
            : t('pages.devices.show.details.display_off');
          return { ...item, label, count: matched?.count || 0 };
        })}
        label={t('pages.devices.filters.display')}
        loading={loading}
        onClick={(_, items) => setFilter({ field: 'displayOn', items })}
        onExpand={() => toggleFilter('displayOn')}
      />
      <FilterCollapsibleList
        Icon={DoNotStep}
        expanded={filters.silence.open}
        items={filters.silence.items.map((item) => {
          const matched = filterCounts?.silence.find((c) => c.filter === String(item.value));
          const label = item.value ? t('common.boolean.true') : t('common.boolean.false');
          return { ...item, label, count: matched?.count || 0 };
        })}
        label={t('pages.devices.filters.silenced')}
        loading={loading}
        onClick={(_, items) => setFilter({ field: 'silence', items })}
        onExpand={() => toggleFilter('silence')}
      />
      {isAdmin && (
        <>
          <FilterCollapsibleList
            Icon={VisibilityOff}
            expanded={filters.internal.open}
            items={filters.internal.items.map((item) => {
              const matched = filterCounts?.internal.find((c) => c.filter === String(item.value));
              const label = item.value ? t('common.boolean.true') : t('common.boolean.false');
              return { ...item, label, count: matched?.count || 0 };
            })}
            label={t('common.labels.internal')}
            loading={loading}
            onClick={(_, items) => setFilter({ field: 'internal', items })}
            onExpand={() => toggleFilter('internal')}
          />
          <FilterCollapsibleList
            Icon={RocketLaunch}
            expanded={filters.demo.open}
            items={filters.demo.items.map((item) => {
              const matched = filterCounts?.demo.find((c) => c.filter === String(item.value));
              const label = item.value ? t('common.boolean.true') : t('common.boolean.false');
              return { ...item, label, count: matched?.count || 0 };
            })}
            label={t('common.labels.demo')}
            loading={loading}
            onClick={(_, items) => setFilter({ field: 'demo', items })}
            onExpand={() => toggleFilter('demo')}
          />
        </>
      )}
    </>
  );
};
